import { Button } from "@/components/flexkit/Button";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { RoleFilterValue, StatusFilterValue } from "@/components/settings/organization/members/members-utils";
import { useMemo, type FC } from "react";

type MembersEmptyStateProps = {
    hasSearchQuery: boolean;
    roleFilter?: RoleFilterValue;
    statusFilter?: StatusFilterValue;
    onResetFilters?: () => void;
};

export const MembersEmptyState: FC<MembersEmptyStateProps> = ({
    hasSearchQuery,
    roleFilter,
    statusFilter,
    onResetFilters,
}) => {
    const hasRoleFilter = roleFilter && roleFilter !== RoleFilterValue.All;
    const hasStatusFilter = statusFilter && statusFilter !== StatusFilterValue.All;
    const hasActiveFilters = hasSearchQuery || hasRoleFilter || hasStatusFilter;

    const emptyState = useMemo(() => {
        // If search query is present, prioritize search empty state
        if (hasSearchQuery) {
            return {
                title: "No results found",
                description: "No members match your search criteria.",
                testId: "no-search-results-empty-state",
            };
        }

        // Status filter empty states
        if (hasStatusFilter && !hasRoleFilter) {
            if (statusFilter === StatusFilterValue.Active) {
                return {
                    title: "No active accounts",
                    description: "You have no active accounts, adjust filter to view more members.",
                    testId: "no-active-accounts-empty-state",
                };
            }
            if (statusFilter === StatusFilterValue.Suspended) {
                return {
                    title: "No suspended accounts",
                    description: "You have no suspended accounts, adjust filter to view more members.",
                    testId: "no-suspended-accounts-empty-state",
                };
            }
        }

        // Role filter empty states
        if (hasRoleFilter && !hasStatusFilter) {
            if (roleFilter === RoleFilterValue.Admin) {
                return {
                    title: "No admins",
                    description: "You have no admins, adjust filter to view more members.",
                    testId: "no-admins-empty-state",
                };
            }
            if (roleFilter === RoleFilterValue.Member) {
                return {
                    title: "No members",
                    description: "You have no members, adjust filter to view more members.",
                    testId: "no-members-empty-state",
                };
            }
        }

        // Combined filters
        if (hasRoleFilter && hasStatusFilter) {
            const roleLabel = roleFilter === RoleFilterValue.Admin ? "admins" : "members";
            const statusLabel =
                statusFilter === StatusFilterValue.Active
                    ? "active"
                    : statusFilter === StatusFilterValue.Suspended
                      ? "suspended"
                      : "";
            return {
                title: `No ${statusLabel} ${roleLabel}`,
                description: `You have no ${statusLabel} ${roleLabel}, adjust filters to view more members.`,
                testId: "no-filtered-results-empty-state",
            };
        }

        return null;
    }, [hasSearchQuery, hasRoleFilter, hasStatusFilter, roleFilter, statusFilter]);

    if (!emptyState) return null;

    return (
        <EmptyStateCard title={emptyState.title} description={emptyState.description} data-testid={emptyState.testId}>
            {hasActiveFilters && onResetFilters && (
                <Button
                    data-tracking-id="reset-filters"
                    variant="secondary"
                    onClick={onResetFilters}
                    LeadingIcon={IconRefresh}
                >
                    Reset filters
                </Button>
            )}
        </EmptyStateCard>
    );
};
